import {
  languageDisplayLabel,
  LanguagesEnum,
} from '@/api/enums/languages.enum';
import {
  EmailAttachment,
  InteractiveElementType,
  Slide,
  SlideImage,
  SlideTemplateOptions,
  SlideTemplateType,
  TextAlignment,
} from '@/client/courses/types/courses.type';
import { DialogContext } from '@/common/context';
import { AttachmentTypeMenu } from '@/components/slides/admin/AttachmentTypeMenu';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { FlexContainer } from '@/ui/styled-ui';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Panel } from 'primereact/panel';
import { TabMenu, TabMenuTabChangeEvent } from 'primereact/tabmenu';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ImageSettingsPanel } from './ImageSettingsPanel';
import { SlideImageUpload } from './SlideImageUpload';

const Sidebar = styled.div`
  background: var(--white-main);
  border-left: 1px solid var(--gray-light);
  padding: 32px var(--default-padding);
  display: flex;
  flex-direction: column;
  min-width: 450px;
  border: 1px solid var(--gray-dark);
  border-top-right-radius: var(--default-border-radius);
  border-bottom-right-radius: var(--default-border-radius);
  overflow: auto;

  h2 {
    font-size: var(--heading-small-font-size);
    color: var(--black-main);
    margin-bottom: var(--default-padding);
  }
`;

const InfoBoxSidebarSection = styled.div`
  margin-bottom: 32px;
`;

const StyledLabel = styled.div`
  font-weight: 500;
  color: var(--black-main);
  font-size: var(--xsmall-font-size);
  margin-bottom: var(--xsmall-padding);
`;

const StyledPanel = styled(Panel)`
  .p-panel-header {
    color: var(--black-main);
    background: transparent;
    border: none;
    padding: 0;
    margin-bottom: var(--medium-padding);
  }

  .p-panel-title {
    font-weight: 600;
    font-size: var(--small-font-size);
  }

  .p-panel-content {
    padding: 0;
    border: none;
    background: transparent;
  }

  .p-panel-toggle-icon {
    color: var(--gray-dark);
    transition: transform 0.2s ease;
  }

  &.p-panel-collapsed .p-panel-toggle-icon {
    transform: rotate(-90deg);
  }

  &:not(:last-child) {
    margin-bottom: var(--medium-padding);
  }

  .border-checkbox {
    color: var(--black-main);
  }

  .slide-type-panel {
    .p-dropdown-items-wrapper {
      display: none !important;
    }
  }
`;

const StyledDropdown = styled(Dropdown)`
  .p-dropdown-label.p-inputtext {
    padding-right: 0px;
    unicode-bidi: normal;
    direction: inherit;
  }
}`;

const StyledMultiSelect = styled(MultiSelect)`
  background: var(--white-main);
  border-color: var(--purple-dark);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  border-radius: var(--default-border-radius);
  gap: var(--xsmall-padding) !important;

  .p-placeholder,
  .p-multiselect-label {
    color: var(--black-main) !important;
    padding-right: 0 !important;
  }

  .p-multiselect-label {
    white-space: nowrap;
  }

  &.p-focus {
    border-color: var(--purple-dark) !important;
    box-shadow: none !important;
  }

  &:hover {
    background: var(--red-dark);
    border-color: var(--red-dark);

    .p-placeholder,
    .p-multiselect-label,
    svg {
      color: var(--white-main) !important;
    }
  }
`;

const StyledOverlayPanel = styled(OverlayPanel)`
  box-shadow: var(--box-shadow-black);
  .p-overlaypanel-content {
    padding: 0;
    border-radius: var(--small-border-radius);
    width: 400px;
    display: flex;
    flex-wrap: wrap;

    .template-option {
      cursor: pointer;
      flex: 1 1 40%;
      font-size: var(--xsmall-font-size);
      line-height: var(--xsmall-line-height);
      font-weight: 500;
      padding: var(--medium-padding);
      transition: all 0.2s ease;
      &:hover {
        background: var(--gray-pale);
      }
    }
  }
`;

const StyledAlignmentTabMenu = styled(TabMenu)`
  height: auto;
  padding: 0;

  .p-tabmenu-nav {
    gap: var(--xsmall-padding);
  }

  .p-tabmenuitem {
    background-color: var(--beige-main);
    padding: var(--xsmall-padding) 7px;
    border-radius: var(--xxxsmall-border-radius);
    transition: all 0.2s ease;

    &.p-highlight {
      .p-menuitem-link {
        color: var(--red-main) !important;

        .p-menuitem-icon {
          color: var(--red-main) !important;
        }
      }
    }

    &:not(.p-highlight):hover {
      background-color: var(--gray-pale);
    }
  }

  .p-menuitem-link {
    padding: 0 !important;
    border: none !important;
    font-weight: 500;
    color: var(--black-main);
    transition: color 0.2s ease;

    .p-menuitem-icon {
      margin: 0;
      border: none !important;
      font-weight: 500;
      color: var(--black-main);
      transition: color 0.2s ease;
    }
  }
`;

const StyledAddImageButton = styled(FlexContainer)`
  border: 1px solid var(--gray-moderate);
  border-radius: var(--xsmall-border-radius);
  padding: var(--small-padding);
  cursor: pointer;
  color: var(--black-main);
  font-weight: 500;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  transition: all 0.2s ease;

  &:hover {
    background: var(--beige-main);
  }
`;

const StyledOption = styled(FlexContainer)`
  color: var(--black-main);
  font-weight: 500;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
`;

type SlideDeckSidebarProps = {
  selectedLanguage: LanguagesEnum;
  courseLanguages: LanguagesEnum[];
  currentSlideData: any;
  popUpSlides: Slide[];
  isEditingPopUpSlide: boolean;
  isCustomContent?: boolean;
  onLanguageChange: (language: LanguagesEnum) => void;
  onSlideUpdate: (slideId: string, updates: Partial<Slide>) => void;
  onCopyToLanguage: (targetLanguages: LanguagesEnum[]) => void;
  onSave: () => void;
};

export const SlideDeckSidebar: React.FC<SlideDeckSidebarProps> = ({
  selectedLanguage,
  courseLanguages,
  currentSlideData,
  popUpSlides,
  isEditingPopUpSlide,
  isCustomContent,
  onLanguageChange,
  onSlideUpdate,
  onCopyToLanguage,
  onSave,
}) => {
  const { t } = useTranslation();
  const { setDialogData } = useContext(DialogContext);
  const account = useAppSelector(selectCurrentAccount);
  const op = useRef<OverlayPanel>(null);

  const [targetLanguagesForCopy, setTargetLanguagesForCopy] = useState<
    LanguagesEnum[]
  >([]);
  const [showCopyLanguages, setShowCopyLanguages] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [openImageSettings, setOpenImageSettings] = useState<number | null>(
    null,
  );
  const getPopupSlideId = (slide: Slide): string | undefined => {
    if (slide.popupSlide) {
      return typeof slide.popupSlide === 'string'
        ? slide.popupSlide
        : slide.popupSlide.id;
    }
    return slide.tempPopupSlideId;
  };

  const handlePopupSlideChange = (selectedValue: string | null | undefined) => {
    if (!currentSlideData.id) return;

    if (!selectedValue) {
      onSlideUpdate(currentSlideData.id, {
        popupSlide: undefined,
        tempPopupSlideId: undefined,
      });
      return;
    }

    const selectedPopupSlide = popUpSlides.find(
      ({ id, tempId }) =>
        (id?.includes('temp-') ? tempId : id) === selectedValue,
    );

    if (selectedPopupSlide) {
      const isNewSlide = selectedPopupSlide.id?.includes('temp-');

      onSlideUpdate(currentSlideData.id, {
        tempPopupSlideId: isNewSlide ? selectedPopupSlide.tempId : undefined,
        popupSlide: isNewSlide ? undefined : selectedPopupSlide.id,
      });
    }
  };

  const targetLanguageOptions = useMemo(() => {
    const targetLanguages = showCopyLanguages
      ? targetLanguagesForCopy
      : courseLanguages;

    return (
      targetLanguages
        ?.filter((lang) => lang !== selectedLanguage)
        ?.map((lang) => ({
          label: languageDisplayLabel[lang],
          value: lang,
        })) || []
    );
  }, [
    showCopyLanguages,
    targetLanguagesForCopy,
    courseLanguages,
    selectedLanguage,
  ]);

  const copyToLanguagesDropdownFooter = () => {
    return (
      <div>
        <button
          className="underline cursor-pointer show-selected-text"
          onClick={() => setShowCopyLanguages(!showCopyLanguages)}
        >
          {showCopyLanguages ? t('generic.showAll') : t('generic.showSelected')}
        </button>
      </div>
    );
  };

  const getCopyToLanguagesMessage = (languages: LanguagesEnum[]) => {
    const languageLabels = languages
      .map((lang) => languageDisplayLabel[lang])
      .join(', ');

    const translation = {
      warning: '',
      note: '',
    };

    if (languages.length === 1) {
      translation.warning = t('content.slides.copyToLanguage.confirm', {
        language: languageLabels,
      });
      translation.note = t('content.slides.copyToLanguage.confirm.note');
    } else {
      translation.warning = t('content.slides.copyToLanguages.confirm', {
        languages: languageLabels,
      });
      translation.note = t('content.slides.copyToLanguages.confirm.note');
    }

    return translation;
  };

  const handleMultiSelectHide = () => {
    if (targetLanguagesForCopy.length > 0) {
      setDialogData({
        type: DialogType.CONFIRMATION,
        show: true,
        closeImmediatelyAfterAccept: true,
        header: t('dialog.confirm'),
        customContent: (
          <p className="m-0">
            {getCopyToLanguagesMessage(targetLanguagesForCopy).warning}
            <br />
            {getCopyToLanguagesMessage(targetLanguagesForCopy).note}
          </p>
        ),
        onAccept: () => {
          onCopyToLanguage(targetLanguagesForCopy);
        },
      });
    }

    setTargetLanguagesForCopy([]);
  };

  const optionTemplate = (item: {
    value: SlideTemplateType;
    label: string;
    image: string;
  }) => {
    return (
      <StyledOption
        direction="column"
        align="center"
        gap={8}
        onClick={() => {
          const updates: Partial<Slide> = {
            templateType: item.value,
            textAlignment: [
              SlideTemplateType.TEXT_IMAGE,
              SlideTemplateType.SUMMARY,
            ].includes(item.value)
              ? TextAlignment.LEFT
              : currentSlideData.textAlignment || TextAlignment.LEFT,
          };

          if (item.value === SlideTemplateType.GALLERY) {
            updates.interactiveElement = null;
          }

          onSlideUpdate(currentSlideData.id, updates);
          op.current?.hide();
        }}
        className="template-option"
      >
        <img src={item.image} alt={item.label} />
        <div>{item.label}</div>
      </StyledOption>
    );
  };
  const imageSettingsTemplate = (image: SlideImage) => {
    return (
      <ImageSettingsPanel
        imageSettings={{
          withBorder: image.withBorder || false,
          withZoom: image.withZoom || false,
        }}
        isOpen={openImageSettings === image.position}
        onOpen={() => setOpenImageSettings(image.position)}
        onClose={() => setOpenImageSettings(null)}
        onBorderChange={(withBorder: boolean) => {
          const existingImages = [...(currentSlideData.images || [])];
          const imageIndex = existingImages.findIndex(
            (img) => img.position === image.position,
          );
          if (imageIndex !== -1 && currentSlideData.id) {
            existingImages[imageIndex] = {
              ...existingImages[imageIndex],
              withBorder,
            };
            onSlideUpdate(currentSlideData.id, {
              images: existingImages,
            });
          }
        }}
        onZoomChange={(withZoom: boolean) => {
          const existingImages = [...(currentSlideData.images || [])];
          const imageIndex = existingImages.findIndex(
            (img) => img.position === image.position,
          );
          if (imageIndex !== -1 && currentSlideData.id) {
            existingImages[imageIndex] = {
              ...existingImages[imageIndex],
              withZoom,
            };
            onSlideUpdate(currentSlideData.id, {
              images: existingImages,
            });
          }
        }}
      />
    );
  };

  const slideAttachment: EmailAttachment | null =
    currentSlideData.interactiveElement?.data?.attachment || null;

  const setSlideAttachment = (attachment: EmailAttachment | null) => {
    if (!currentSlideData.id) return;

    const data = currentSlideData.interactiveElement?.data || {};

    onSlideUpdate(currentSlideData.id, {
      interactiveElement: {
        ...currentSlideData.interactiveElement,
        data: {
          ...data,
          attachment: attachment || undefined,
        },
      },
    });
  };

  return (
    <Sidebar>
      <h2 className="mt-0">{t('content.presentation.editor')}</h2>

      {/* Language Settings */}
      <StyledPanel
        header={t('content.language.settings')}
        toggleable
        collapsed={false}
        className="w-full"
        collapseIcon="pi pi-chevron-up"
        expandIcon="pi pi-chevron-down"
      >
        <FlexContainer style={{ marginBottom: 16 }} gap={8}>
          <StyledDropdown
            value={selectedLanguage}
            options={courseLanguages?.map((lang: LanguagesEnum) => ({
              label: languageDisplayLabel[lang],
              value: lang,
            }))}
            onChange={(e) => onLanguageChange(e.value)}
            className="w-full"
          />

          <StyledMultiSelect
            value={targetLanguagesForCopy}
            options={targetLanguageOptions}
            onChange={(e) => setTargetLanguagesForCopy(e.value)}
            placeholder={t('generic.copyTo')}
            selectAllLabel={t('content.select.all.languages')}
            maxSelectedLabels={0}
            selectedItemsLabel={t('generic.copyTo')}
            panelFooterTemplate={copyToLanguagesDropdownFooter}
            onHide={handleMultiSelectHide}
          />
        </FlexContainer>
      </StyledPanel>

      {/* Template Settings */}
      <StyledPanel
        header={t('content.template.settings')}
        toggleable
        collapsed={false}
        className="w-full"
        collapseIcon="pi pi-chevron-up"
        expandIcon="pi pi-chevron-down"
      >
        <AppButton
          className="w-full mb-3"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            op.current?.toggle(e)
          }
          label={
            currentSlideData.templateType
              ? t(`content.slide.template.${currentSlideData.templateType}`)
              : t('content.slide.template.text-image')
          }
          icon="pi pi-chevron-down"
          iconPos="right"
          severity="dropdown"
        />

        {/* Template Type Selection */}
        <StyledOverlayPanel ref={op} showCloseIcon={false}>
          {SlideTemplateOptions(t).map((option) => optionTemplate(option))}
        </StyledOverlayPanel>

        <FlexContainer direction="column" gap={18}>
          {currentSlideData.images && currentSlideData.images.length > 0 ? (
            currentSlideData.images
              ?.filter((image: SlideImage) =>
                currentSlideData.templateType !== SlideTemplateType.GALLERY
                  ? image.position === 0
                  : true,
              )
              .map((image: SlideImage) => (
                <React.Fragment
                  key={`${selectedLanguage}-${
                    currentSlideData.id || 'current'
                  }-image-${image.position}`}
                >
                  <SlideImageUpload
                    preselectedFile={image.file}
                    preselectedInteractiveElement={
                      currentSlideData.interactiveElement || null
                    }
                    setIsSubmitDisabled={setIsUploading}
                    additionalImgOptions={
                      <>
                        {imageSettingsTemplate(image)}
                        {currentSlideData.interactiveElement?.type ===
                          InteractiveElementType.EMAIL && (
                          <AttachmentTypeMenu
                            attachment={slideAttachment}
                            onChange={setSlideAttachment}
                            menuId={`slide-attachment-type-menu-${
                              currentSlideData.id || 'new'
                            }-${image.position}`}
                            triggerClassName="m-auto"
                          />
                        )}
                      </>
                    }
                    slideId={currentSlideData.id}
                    templateType={currentSlideData.templateType}
                    onInteractiveElementAdd={(slideId, interactiveElement) => {
                      onSlideUpdate(slideId, {
                        interactiveElement,
                        // Clear images when adding interactive element
                        images: undefined,
                      });
                    }}
                    onFileUpload={(file) => {
                      if (!currentSlideData.id) return;

                      const existingImages = [
                        ...(currentSlideData.images || []),
                      ];

                      if (file) {
                        // Update existing image
                        const imageIndex = existingImages.findIndex(
                          (img) => img.position === image.position,
                        );
                        if (imageIndex !== -1) {
                          existingImages[imageIndex] = {
                            ...existingImages[imageIndex],
                            file,
                          };
                          onSlideUpdate(currentSlideData.id, {
                            images: existingImages,
                            // Clear interactive element when uploading image
                            interactiveElement: undefined,
                          });
                        }
                      } else {
                        // Remove image and reindex positions
                        const filteredImages = existingImages
                          .filter((img) => img.position !== image.position)
                          .map((img, index) => ({ ...img, position: index }));

                        onSlideUpdate(currentSlideData.id, {
                          images:
                            filteredImages.length > 0
                              ? filteredImages
                              : undefined,
                          // Clear interactive element when clearing image
                          interactiveElement: undefined,
                        });
                      }
                    }}
                  />
                </React.Fragment>
              ))
          ) : (
            <>
              <SlideImageUpload
                key={`${selectedLanguage}-${
                  currentSlideData.id || 'current'
                }-image`}
                preselectedFile={currentSlideData.image}
                preselectedInteractiveElement={
                  currentSlideData.interactiveElement || null
                }
                setIsSubmitDisabled={setIsUploading}
                additionalImgOptions={
                  currentSlideData.interactiveElement?.type ===
                  InteractiveElementType.EMAIL ? (
                    <AttachmentTypeMenu
                      attachment={slideAttachment}
                      onChange={setSlideAttachment}
                      menuId={`slide-attachment-type-menu-${
                        currentSlideData.id || 'new'
                      }-0`}
                      triggerClassName="m-auto"
                    />
                  ) : null
                }
                slideId={currentSlideData.id}
                templateType={currentSlideData.templateType}
                onInteractiveElementAdd={(slideId, interactiveElement) => {
                  onSlideUpdate(slideId, {
                    interactiveElement,
                    // Clear images when adding interactive element
                    images: undefined,
                  });
                }}
                onFileUpload={(file) => {
                  if (file && currentSlideData.id) {
                    onSlideUpdate(currentSlideData.id, {
                      images: [{ file, position: 0, withBorder: false }],
                      // Clear interactive element when uploading image
                      interactiveElement: undefined,
                    });
                  } else if (currentSlideData.id) {
                    onSlideUpdate(currentSlideData.id, {
                      images: [{ file: null, position: 0, withBorder: false }],
                      interactiveElement: undefined,
                    });
                  }
                }}
              />
            </>
          )}
          {SlideTemplateType.GALLERY === currentSlideData.templateType && (
            <StyledAddImageButton
              direction="column"
              gap={8}
              onClick={() => {
                const existingImages = currentSlideData.images || [];
                const nextPosition =
                  existingImages.length > 0
                    ? Math.max(
                        ...existingImages.map(
                          (img: SlideImage) => img.position,
                        ),
                      ) + 1
                    : 0;
                onSlideUpdate(currentSlideData.id, {
                  images: [
                    ...existingImages,
                    {
                      file: null,
                      position: nextPosition,
                      withBorder: true,
                      withZoom: false,
                    },
                  ],
                });
              }}
            >
              <i className="pi pi-plus" />
              <div>{t('content.image.upload.add.image')}</div>
            </StyledAddImageButton>
          )}
        </FlexContainer>

        {/* Text Alignment Settings */}
        {[SlideTemplateType.CHAPTER, SlideTemplateType.GALLERY].includes(
          currentSlideData.templateType,
        ) && (
          <FlexContainer direction="column" align="flex-start" className="mt-3">
            <StyledLabel>{t('content.text.alignment')}</StyledLabel>
            <StyledAlignmentTabMenu
              key={`alignment-${currentSlideData.id}-${
                currentSlideData.textAlignment || 'left'
              }`}
              activeIndex={
                currentSlideData.textAlignment === TextAlignment.CENTER ? 1 : 0
              }
              onTabChange={(e: TabMenuTabChangeEvent) => {
                const newAlignment =
                  e.index === 1 ? TextAlignment.CENTER : TextAlignment.LEFT;
                onSlideUpdate(currentSlideData.id, {
                  textAlignment: newAlignment,
                });
              }}
              model={[
                {
                  icon: 'pi pi-align-left',
                },
                {
                  icon: 'pi pi-align-center',
                },
              ]}
            />
          </FlexContainer>
        )}
      </StyledPanel>

      {/* Info Box Settings */}
      {!isEditingPopUpSlide &&
        [SlideTemplateType.TEXT_IMAGE, SlideTemplateType.SUMMARY].includes(
          currentSlideData.templateType,
        ) && (
          <StyledPanel
            header={t('content.info.box')}
            toggleable
            collapsed={false}
            className="w-full"
            collapseIcon="pi pi-chevron-up"
            expandIcon="pi pi-chevron-down"
          >
            <InfoBoxSidebarSection>
              <StyledLabel>{t('content.info.box.text')}</StyledLabel>
              <InputText
                className="w-full"
                placeholder={t('content.enter.info.box.text')}
                style={{ marginBottom: 20 }}
                value={currentSlideData.infobox || ''}
                onChange={(e) => {
                  if (currentSlideData.id) {
                    onSlideUpdate(currentSlideData.id, {
                      infobox: e.target.value,
                    });
                  }
                }}
              />
              <StyledLabel>{t('content.link.to.slide')}</StyledLabel>
              <Dropdown
                value={getPopupSlideId(currentSlideData)}
                optionValue="value"
                options={popUpSlides.map((s) => ({
                  label: s.headline?.replace(/<[^>]*>?/g, ''),
                  value: s?.id?.includes('temp-') ? s.tempId : s.id,
                }))}
                placeholder={t('content.select.info.box.slide')}
                onChange={(e) => handlePopupSlideChange(e.value)}
                showClear
                appendTo={document.body}
                className="w-full"
              />
            </InfoBoxSidebarSection>
          </StyledPanel>
        )}

      {/* AI Instructions — system account only, original content only */}
      {account?.isSystem && !isCustomContent && (
        <StyledPanel
          header={
            <FlexContainer justify="flex-start" align="center" gap={0}>
              {t('content.slide.aiCustomizationInstructions')}
              <AppIconTooltip
                target="ai-instructions-tooltip"
                content={t('content.slide.aiCustomizationInstructions.tooltip')}
              />
            </FlexContainer>
          }
          toggleable
          collapsed={false}
          className="w-full"
          collapseIcon="pi pi-chevron-up"
          expandIcon="pi pi-chevron-down"
        >
          <FlexContainer
            justify="space-between"
            align="center"
            className="mb-3"
          >
            <StyledLabel>
              {currentSlideData.isAiCustomizationEnabled !== false
                ? t('content.slide.aiCustomizationInstructions.enabled')
                : t('content.slide.aiCustomizationInstructions.disabled')}
            </StyledLabel>
            <InputSwitch
              checked={currentSlideData.isAiCustomizationEnabled !== false}
              onChange={(e) => {
                if (currentSlideData.id) {
                  onSlideUpdate(currentSlideData.id, {
                    isAiCustomizationEnabled: e.value,
                  });
                }
              }}
            />
          </FlexContainer>
          {currentSlideData.isAiCustomizationEnabled !== false && (
            <InputTextarea
              className="w-full"
              rows={4}
              placeholder={t(
                'content.slide.aiCustomizationInstructions.placeholder',
              )}
              value={currentSlideData.aiCustomizationInstructions || ''}
              onChange={(e) => {
                if (currentSlideData.id) {
                  onSlideUpdate(currentSlideData.id, {
                    aiCustomizationInstructions: e.target.value,
                  });
                }
              }}
            />
          )}
        </StyledPanel>
      )}

      {/* Actions */}
      <AppButton
        label={t('content.save.presentation')}
        style={{ marginTop: 'auto' }}
        isDisabled={isUploading}
        onClick={onSave}
      />
    </Sidebar>
  );
};
